import { createContext, useContext, type ReactNode } from "react";
import { useMyTaskDashboard, type TaskDashboardSnapshot } from "@/hooks/useMyTaskDashboard";
import { useMemberPermissions } from "@/hooks/useMemberPermissions";

const TaskDashboardContext = createContext<TaskDashboardSnapshot | undefined>(undefined);

const DISABLED: TaskDashboardSnapshot = {
  counts: {
    overdue: 0,
    dueToday: 0,
    approaching: 0,
    completedToday: 0,
    pendingActionable: 0,
    filterToday: 0,
    filterOverdue: 0,
    filterUpcoming: 0,
  },
  dayProgress: { done: 0, total: 0, percent: 0 },
  weekProgress: { percent: 0 },
  motivationalLabel: "",
  hasUrgentAttention: false,
  pendingTotal: 0,
  showMissionStrip: false,
  loading: false,
  refetch: async () => {},
};

export function TaskDashboardProvider({
  workspaceId,
  disabled = false,
  children,
}: {
  workspaceId: string | null;
  disabled?: boolean;
  children: ReactNode;
}) {
  const { memberId, canAccessTasks } = useMemberPermissions();
  const dashboard = useMyTaskDashboard(workspaceId, memberId, disabled || !canAccessTasks);

  return (
    <TaskDashboardContext.Provider value={disabled ? DISABLED : dashboard}>
      {children}
    </TaskDashboardContext.Provider>
  );
}

export function useTaskDashboard(): TaskDashboardSnapshot {
  const ctx = useContext(TaskDashboardContext);
  if (ctx === undefined) {
    throw new Error("useTaskDashboard must be used within TaskDashboardProvider");
  }
  return ctx;
}
